<template>
    <div
        ref="resizeRef"
        class="app"
        :class="{ 'auto-height': appAutoHeightOn }"
    >
        <Transition :name="widgetOpenedAnimation">
            <div
                v-if="widgetOpened"
                class="widget-wrapper"
                :class="{
                    'widget-opened': widgetOpened,
                    'widget--inline': widgetOptions.inline,
                    'widget--dialog': !widgetOptions.inline
                }"
            >
                <div
                    ref="widgetRef"
                    class="widget-card"
                    :class="{ 'widget-card--image-background': hasImageBackground }"
                >
                    <CloseWidgetButton />
                    <Component
                        :is="currentViewComponent"
                        v-if="currentViewComponent"
                        :key="currentViewOptions?.nonce"
                        :options="currentViewOptions"
                        :data-testid="currentViewTestId"
                        @change-view="changeView"
                    />
                    <NotificationsCenter />
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import useEmbed from '@/embed/code'
import CloseWidgetButton from './components/CloseWidgetButton.vue'
import useWidgetViewManager from '@/composables/widget-view-manager'
import useWidgetAutoHeight from './composables/widget-auto-height/useWidgetAutoHeight'
import { useEventBus } from '@vueuse/core'
import { createTLI18n } from '@/composables/i18n'
import useWidgetHooks from './composables/widget-hooks'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { createOrchestratorApi } from './api/orchestrator'
import { createAppsApi } from './api/apps'
import NotificationsCenter from './components/NotificationsCenter.vue'
import useWidgetOptions from './composables/widget-options'
import isIframe from './embed/helpers/is-iframe'
import { kebabCase } from 'change-case'
import { useCustomCss } from './composables/custom-css'
import useCloseWidgetButton from '@/composables/widget-close-button'
import { installBreakpoints } from 'widget-components/composables/breakpoints'

import { logger } from '@sentry/vue'

const resizeRef = ref<HTMLElement | undefined>()
const widgetRef = ref<HTMLElement | undefined>()

installBreakpoints(widgetRef)

const { closeWidget } = useCloseWidgetButton()
const { currentViewOptions, changeView, currentViewComponent, currentView } = useWidgetViewManager()
const { options: widgetOptions } = useWidgetOptions()
const { preInitialize, initialize, widgetOpened } = useWidgetHooks()
const { appAutoHeightOn, runAppAutoHeight } = useWidgetAutoHeight()
const { hasImageBackground } = useCustomCss()

const params = new URLSearchParams(window.location.search)

const widgetOpenedAnimation = computed(() => {
    const { customPagePreview, inline } = widgetOptions.value

    if (customPagePreview || inline) {
        return
    }
    return 'widget-opened'
})

const getToken = (): string | null => {
    const urlToken = params.get('token')

    if (urlToken) {
        return urlToken
    }

    const envToken = import.meta.env.VITE_WIDGET_TOKEN
    if (import.meta.env.DEV && import.meta.env.VITE_WIDGET_TOKEN) {
        return envToken
    }

    return null
}

const token = getToken()

const currentViewTestId = computed(() => {
    return `widget-page-${currentView.value ? kebabCase(currentView.value) : 'no-view'}`
})

const handleKeydown = (event: KeyboardEvent) => {
    if (!widgetOptions.value.inline && event.key === 'Escape') {
        closeWidget()
    }
}

onMounted(async () => {
    await createTLI18n(window.navigator.language)

    if (!isIframe && token) {
        createOrchestratorApi()
        createAppsApi()
        await preInitialize(token)
        initialize()
    } else {
        useEmbed().initializeIframe()
    }

    if (widgetOptions.value.autoHeight) {
        runAppAutoHeight(resizeRef)
    }

    useEventBus('app-auto-height-request').on(() => runAppAutoHeight(resizeRef))

    window.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeydown)
    logger.info('Widget is unmounting')
})
</script>

<style src="@/styles/main.scss" lang="scss"></style>

<style scoped lang="scss">

.widget-wrapper, .app {
    pointer-events: all;
    width: 100%;
    height: 100%;

    &.widget-opened {
        background-color: rgba(0, 0, 0, 0.5);
        backdrop-filter: saturate(180%) blur(3px);
    }
}

.widget-wrapper {
    overflow: hidden;
}

.app {
    &.auto-height {
        height: auto;
    }
}

.widget {
    &--dialog {
        display: flex;
        align-items: center;
        justify-content: center;

        .widget-card {
            position: relative;
            --card-width: 420px;
            --card-max-height: 580px;
            width: var(--card-width);
            height: 100%;
            max-height: var(--card-max-height);
            border-radius: var(--widget-border-radius);
            overflow: hidden;
            display: flex;
            flex-flow: column;
            transition: 0s;
            box-shadow: 0 10px 50px -20px rgba(0, 0, 0, 0.3);
            @media (max-width: 460px) {
                max-height: none;
                border-radius: 0;
            }
        }

        // if hoisted block is empty, add padding to content (BC); hoisted block reserves space at top itself
        :deep(.blocks-hoisted:empty ~ .widget-card-content),
        :deep(.blocks-hoisted:has(> :empty) ~ .widget-card-content) {
            padding-top: 54px;
        }
    }

    &--inline {
        --widget-border-radius: 0;
        backdrop-filter: none !important;
        box-shadow: none !important;
        perspective: unset !important;

        .widget-card {
            position: relative;
            display: flex;
            flex-flow: column;
            transition: 0s;
            height: 100%;
        }

        :deep(.widget-card-content:has(.flex-layout)) {
            padding: 0
        }
    }
}
</style>
